import { useEffect, useMemo, useState } from 'react'
import { Col, Label, Pagination, PaginationItem, PaginationLink, Row } from 'reactstrap'
import Select from 'react-select'
import DatePicker from 'react-datepicker'
import Loader from 'react-loaders'
import moment from 'moment'
import { request } from '../../../../Redux/Sagas/requests/api'
import { primary_color } from '../../../../Helper/uiHelper'
import ReportEmailSchedulePanel from '../../../Common/ReportEmailSchedule/reportEmailSchedulePanel'
import ReportDownloadButtons from '../../../Common/ReportDownload/reportDownloadButtons'
import './schoolSalesComparisonReport.scss'

const PAGE_SIZE_OPTIONS = [
  { value: 25, label: '25 / page' },
  { value: 50, label: '50 / page' },
  { value: 100, label: '100 / page' },
]

const selectStyles = {
  menu: (base) => ({ ...base, zIndex: 20 }),
}

const SchoolSalesComparisonReport = ({ cmsKey }) => {
  const [reportDate, setReportDate] = useState(new Date())
  const [domainIds, setDomainIds] = useState([])
  const [report, setReport] = useState(null)
  const [schools, setSchools] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  const schoolOptions = useMemo(
    () => schools.map((s) => ({ value: String(s.id), label: s.domain_name })),
    [schools]
  )
  const selectedSchools = schoolOptions.filter((o) => domainIds.includes(o.value))
  const selectedPageSize = PAGE_SIZE_OPTIONS.find((o) => o.value === pageSize) || PAGE_SIZE_OPTIONS[0]

  const load = () => {
    setLoading(true)
    const params = { date: moment(reportDate).format('YYYY-MM-DD') }
    if (domainIds.length) params.domain_ids = domainIds
    request('reports/school-sales-comparison', 'GET', {}, {}, params).then((res) => {
      setLoading(false)
      if (res?.status === 200) {
        setReport(res?.data?.data || null)
        setSchools(res?.data?.schools || [])
        setPage(1)
      }
    })
  }

  useEffect(() => {
    load()
  }, [reportDate, domainIds])

  const rows = report?.rows || []
  const totalRows = rows.length
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize))
  const currentPage = Math.min(page, totalPages)
  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return rows.slice(start, start + pageSize)
  }, [rows, currentPage, pageSize])

  const pageNumbers = useMemo(() => {
    const maxButtons = 7
    if (totalPages <= maxButtons) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    const start = Math.max(1, Math.min(currentPage - 3, totalPages - maxButtons + 1))
    return Array.from({ length: maxButtons }, (_, i) => start + i)
  }, [currentPage, totalPages])

  const pctClass = (n) => (n !== null && n !== undefined && n < 0 ? 'neg' : '')
  const cellClass = (...flags) => flags.filter(Boolean).join(' ') || undefined

  return (
    <div className='school-sales-comparison-report p-3'>
      <Row className='mb-3 align-items-end'>
        <Col md={3}>
          <Label className='subtitle1 font-weight-bold' style={{ color: primary_color }}>
            Date
          </Label>
          <DatePicker
            className='form-control'
            selected={reportDate}
            onChange={(d) => d && setReportDate(d)}
            dateFormat='dd/MM/yyyy'
            maxDate={new Date()}
          />
        </Col>
        <Col md={5}>
          <Label className='subtitle1 font-weight-bold' style={{ color: primary_color }}>
            Schools
          </Label>
          <Select
            options={schoolOptions}
            value={selectedSchools}
            onChange={(opts) => setDomainIds((opts || []).map((o) => o.value))}
            styles={selectStyles}
            isMulti
            isClearable
            isSearchable
            placeholder='All schools'
            closeMenuOnSelect={false}
          />
        </Col>
        <Col md={4} className='d-flex justify-content-end align-items-center flex-wrap'>
          <ReportDownloadButtons
            exportUrl='reports/school-sales-comparison/export'
            params={{
              date: moment(reportDate).format('YYYY-MM-DD'),
              ...(domainIds.length ? { domain_ids: domainIds } : {}),
              ...(cmsKey ? { cms_key: cmsKey } : {}),
            }}
            fileBaseName='bookings-by-school'
          />
          <ReportEmailSchedulePanel
            reportKey='school_sales_comparison'
            buttonLabel='Manage emails'
            defaultScheduleName='Bookings By School'
          />
        </Col>
      </Row>

      {loading || !report ? (
        <div className='d-flex justify-content-center' style={{ minHeight: '12rem', alignItems: 'center' }}>
          <Loader type='ball-pulse' color={primary_color} />
        </div>
      ) : (
        <>
          <div className='d-flex justify-content-between align-items-center mb-2 flex-wrap'>
            <div className='text-muted subtitle1'>
              Showing {totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1}–
              {Math.min(currentPage * pageSize, totalRows)} of {totalRows} schools
            </div>
            <div style={{ minWidth: '9rem' }}>
              <Select
                options={PAGE_SIZE_OPTIONS}
                value={selectedPageSize}
                onChange={(opt) => {
                  setPageSize(opt?.value || 25)
                  setPage(1)
                }}
                styles={selectStyles}
                isSearchable={false}
              />
            </div>
          </div>

          <div className='table-responsive'>
            <table className='ssc-table'>
              <thead>
                <tr>
                  <th className='school-col' rowSpan={2}>
                    School
                  </th>
                  <th rowSpan={2}>Doors</th>
                  <th colSpan={2}>Whole Year {report.prev_ay_label}</th>
                  <th colSpan={2}>Whole Year {report.curr_ay_label}</th>
                  <th colSpan={3} className='period-group'>
                    {report.period_label}
                  </th>
                </tr>
                <tr>
                  <th>Sold</th>
                  <th>%</th>
                  <th>Sold</th>
                  <th>%</th>
                  <th className='period-group'>{report.period_prior_year}</th>
                  <th className='period-group'>{report.period_curr_year}</th>
                  <th className='period-group'>{report.period_diff_label}</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <tr key={row.domain_id}>
                    <td className='school-col'>{row.school}</td>
                    <td>{row.doors}</td>
                    <td className={cellClass(row.prev_year?.sold_low && 'low-value')}>{row.prev_year?.sold ?? 0}</td>
                    <td className={cellClass(row.prev_year?.pct_peak && 'peak-value', row.prev_year?.pct_low && 'low-value')}>
                      {row.prev_year?.pct !== null && row.prev_year?.pct !== undefined ? `${row.prev_year.pct}%` : ''}
                    </td>
                    <td className={cellClass(row.curr_year?.sold_low && 'low-value')}>{row.curr_year?.sold ?? 0}</td>
                    <td className={cellClass(row.curr_year?.pct_peak && 'peak-value', row.curr_year?.pct_low && 'low-value')}>
                      {row.curr_year?.pct !== null && row.curr_year?.pct !== undefined ? `${row.curr_year.pct}%` : ''}
                    </td>
                    <td className={cellClass(row.period_prior_peak && 'peak-value', row.period_prior_low && 'low-value')}>
                      {row.period_prior ?? 0}
                    </td>
                    <td className={cellClass(row.period_curr_peak && 'peak-value', row.period_curr_low && 'low-value')}>
                      {row.period_curr ?? 0}
                    </td>
                    <td className={pctClass(row.period_diff_pct)}>
                      {row.period_diff_pct !== null && row.period_diff_pct !== undefined ? `${row.period_diff_pct}%` : ''}
                    </td>
                  </tr>
                ))}
                {report.totals ? (
                  <tr className='totals-row'>
                    <td className='school-col'>Totals (all schools)</td>
                    <td>{report.totals.doors ?? 0}</td>
                    <td>{report.totals.prev_sold ?? 0}</td>
                    <td>
                      {report.totals.prev_pct !== null && report.totals.prev_pct !== undefined ? `${report.totals.prev_pct}%` : ''}
                    </td>
                    <td>{report.totals.curr_sold ?? 0}</td>
                    <td>
                      {report.totals.curr_pct !== null && report.totals.curr_pct !== undefined ? `${report.totals.curr_pct}%` : ''}
                    </td>
                    <td>{report.totals.period_prior ?? 0}</td>
                    <td>{report.totals.period_curr ?? 0}</td>
                    <td className={pctClass(report.totals.period_diff_pct)}>
                      {report.totals.period_diff_pct !== null && report.totals.period_diff_pct !== undefined
                        ? `${report.totals.period_diff_pct}%`
                        : ''}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {totalPages > 1 ? (
            <div className='d-flex justify-content-center mt-3'>
              <Pagination aria-label='Bookings by school pagination' style={{ flexWrap: 'wrap' }}>
                <PaginationItem disabled={currentPage <= 1}>
                  <PaginationLink previous href='#' onClick={(e) => { e.preventDefault(); setPage(currentPage - 1) }} />
                </PaginationItem>
                {pageNumbers.map((n) => (
                  <PaginationItem active={n === currentPage} key={n}>
                    <PaginationLink
                      href='#'
                      onClick={(e) => {
                        e.preventDefault()
                        setPage(n)
                      }}>
                      {n}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem disabled={currentPage >= totalPages}>
                  <PaginationLink next href='#' onClick={(e) => { e.preventDefault(); setPage(currentPage + 1) }} />
                </PaginationItem>
              </Pagination>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}

export default SchoolSalesComparisonReport
